"use client";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { toast } from "sonner";
import {
  User,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
} from "firebase/auth";
import { auth } from "@/lib/firebase/config";
import { useRouter } from "next/navigation";
import { UserAccount } from "@/features/users/user.type";
import { syncLocalCustomerCartToServer } from "@/features/shop/cart/customer-cart-sync";

interface AuthContextType {
  user: User | null;
  userAccount: UserAccount | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  changePassword: (
    currentPassword: string,
    newPassword: string,
  ) => Promise<void>;
  refreshUserAccount: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [userAccount, setUserAccount] = useState<UserAccount | null>(null);
  const [loading, setLoading] = useState(true);
  const router = useRouter();

  const requireAuth = () => {
    if (!auth) throw new Error("Firebase Auth is not initialized.");
    return auth;
  };

  const signOut = useCallback(async () => {
    await firebaseSignOut(requireAuth());
    setUserAccount(null);
    setUser(null);
    router.replace("/login");
  },[router])

  const fetchUserAccount = useCallback(
    async (currentUser: User) => {
      try {
        const token = await currentUser.getIdToken();
        const response = await fetch("/api/auth/me", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setUserAccount(data.user);
          if (data.user?.role === "customer") {
            void syncLocalCustomerCartToServer({ role: data.user.role }).catch(
              (error) => {
                console.error("Failed to sync local cart after login:", error);
                toast.error("Failed to sync your cart to your account");
              },
            );
          }
        } else {
          setUserAccount(null);
          await signOut();
          router.replace("/login");
        }
      } catch (error) {
        console.error("Failed to fetch user profile:", error);
        toast.error("Failed to load your account. Please try again.");
        setUserAccount(null);
      }
    },
    [router, signOut],
  );

  useEffect(() => {
    if (!auth) return;

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setUser(user);
      if (user) {
        await fetchUserAccount(user);
      } else {
        setUserAccount(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, [fetchUserAccount]);

  const signIn = async (email: string, password: string) => {
    await signInWithEmailAndPassword(requireAuth(), email, password);
  };

  const resetPassword = async (email: string) => {
    await sendPasswordResetEmail(requireAuth(), email);
  };

  const changePassword = async (
    currentPassword: string,
    newPassword: string,
  ) => {
    if (!user || !user.email) {
      throw new Error("No authenticated user found.");
    }

    const credential = EmailAuthProvider.credential(
      user.email,
      currentPassword,
    );
    await reauthenticateWithCredential(user, credential);
    await updatePassword(user, newPassword);
  };

  const refreshUserAccount = async () => {
    if (user) {
      await fetchUserAccount(user);
    }
  };

  const value = {
    user,
    userAccount,
    loading,
    signIn,
    signOut,
    resetPassword,
    changePassword,
    refreshUserAccount,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
